export function FactorsAffectingValue() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Additional Factors Affecting Apartment Building Value
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Beyond the primary valuation methods and property condition, numerous other factors influence your apartment building's worth. Understanding these variables helps you maximize value and set realistic expectations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-map-pin-line text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Location Quality</h3>
            <p className="text-gray-600 leading-relaxed mb-3">
              Location is the single most important factor in real estate. Properties in desirable neighborhoods with good schools, low crime, and strong employment command significant premiums.
            </p>
            <p className="text-sm text-gray-500">Impact: 20-50% value difference</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-home-smile-line text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Unit Mix & Size</h3>
            <p className="text-gray-600 leading-relaxed mb-3">
              The combination of studio, 1BR, 2BR, and 3BR units affects marketability and value. Larger units typically generate higher total rent but may have longer vacancy periods.
            </p>
            <p className="text-sm text-gray-500">Impact: 10-20% value difference</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-percent-line text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Occupancy Rate</h3>
            <p className="text-gray-600 leading-relaxed mb-3">
              Higher occupancy means more income and lower risk. Properties with 95%+ occupancy are valued higher than those with significant vacancy.
            </p>
            <p className="text-sm text-gray-500">Impact: 5-15% value difference</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-file-text-line text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Lease Terms</h3>
            <p className="text-gray-600 leading-relaxed mb-3">
              Long-term leases with quality tenants provide income stability. Month-to-month tenants or expiring leases create uncertainty that reduces value.
            </p>
            <p className="text-sm text-gray-500">Impact: 5-10% value difference</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-money-dollar-circle-line text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Rent Levels</h3>
            <p className="text-gray-600 leading-relaxed mb-3">
              Properties with below-market rents offer upside potential. At-market or above-market rents provide immediate income but less growth opportunity.
            </p>
            <p className="text-sm text-gray-500">Impact: 10-30% value difference</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-line-chart-line text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Operating Expenses</h3>
            <p className="text-gray-600 leading-relaxed mb-3">
              Lower operating expenses mean higher NOI and greater value. Properties with efficient operations and lower expense ratios command premiums.
            </p>
            <p className="text-sm text-gray-500">Impact: 15-25% value difference</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-car-line text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Parking Availability</h3>
            <p className="text-gray-600 leading-relaxed mb-3">
              Adequate parking is essential in most markets. Properties with insufficient parking face tenant retention issues and lower values.
            </p>
            <p className="text-sm text-gray-500">Impact: 5-15% value difference</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-star-line text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Amenities</h3>
            <p className="text-gray-600 leading-relaxed mb-3">
              Modern amenities like fitness centers, pools, and in-unit laundry increase desirability and allow for higher rents, boosting property value.
            </p>
            <p className="text-sm text-gray-500">Impact: 5-15% value difference</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-calendar-line text-teal-600 text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Property Age</h3>
            <p className="text-gray-600 leading-relaxed mb-3">
              Newer properties require less maintenance and have modern features. Older properties may have charm but typically need more capital investment.
            </p>
            <p className="text-sm text-gray-500">Impact: 10-25% value difference</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">How These Factors Work Together</h3>
          <p className="text-lg text-gray-700 mb-10 text-center max-w-4xl mx-auto leading-relaxed">
            These factors don't exist in isolation—they interact and compound to create your property's total value. Here's how different combinations affect valuation:
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border-2 border-green-600">
              <h4 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-arrow-up-circle-fill text-green-600 text-3xl"></i>
                Premium Value Property
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>Prime location with strong demographics</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>Excellent condition, recently renovated</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>95%+ occupancy with quality tenants</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>At or above market rents</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>Low operating expense ratio</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span>Modern amenities and ample parking</span>
                </li>
              </ul>
              <p className="mt-4 text-lg font-bold text-green-700">Result: Commands 40-60% premium over average properties</p>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-6 border-2 border-red-600">
              <h4 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-arrow-down-circle-fill text-red-600 text-3xl"></i>
                Discounted Value Property
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span>Secondary location with challenges</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span>Poor condition, deferred maintenance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span>Below 85% occupancy or problem tenants</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span>Below market rents with limited upside</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span>High operating expense ratio</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span>Limited amenities and parking issues</span>
                </li>
              </ul>
              <p className="mt-4 text-lg font-bold text-red-700">Result: Sells at 30-50% discount from average properties</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}